import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import * as Joi from "joi";
import moment from "moment";
import { Select, TextInput } from "flowbite-react";
import { FieldLabel } from "../../../atoms/FieldLabel";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { CustomButton } from "../../../atoms/CustomButton";
import SideDrawer from "../../../atoms/SideDrawer";
import { HiOutlineUser } from "react-icons/hi";
import { CustomPhoneNumber } from "../../../atoms/CustomPhoneNumber";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import NotesPanel from "../../../molecules/NotesPanel";
import {
  Lead,
  LeadColumn,
  UpdateLeadInput,
  LEAD_FIELD_LIMITS,
  MAX_NEXT_ACTION_LENGTH,
} from "../../../../types/LeadTypes";
import { columnLabel, resolveColumnId } from "./leadColumns";
import { leadIdentitySchema } from "./leadSchema";
import AddToAgendaButton from "../agenda/AddToAgendaButton";
import { leadDraft } from "../agenda/appointmentDraft";

interface Props {
  lead: Lead | null;
  columns: LeadColumn[];
  onClose: () => void;
  onSave: (patch: UpdateLeadInput, done: () => void) => void;
  onConvert: (lead: Lead) => void;
  isSaving?: boolean;
}

interface DetailForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  column_id: string;
  next_action: string;
  next_action_at: string;
}

const emptyDetail: DetailForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  column_id: "",
  next_action: "",
  next_action_at: "",
};

const schema = Joi.object<DetailForm>({
  ...leadIdentitySchema,
  column_id: Joi.string().allow(""),
  next_action: Joi.string()
    .trim()
    .max(MAX_NEXT_ACTION_LENGTH)
    .allow("")
    .label("Next action"),
  next_action_at: Joi.string().allow(""),
});

const LeadDetailModal: React.FC<Props> = ({
  lead,
  columns,
  onClose,
  onSave,
  onConvert,
  isSaving = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const canWrite = permissions.can(Cap.LEADS, Level.WRITE);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { errors, dirtyFields },
  } = useForm<DetailForm>({
    mode: "onChange",
    resolver: joiResolver(schema),
    defaultValues: emptyDetail,
  });

  useEffect(() => {
    reset(
      lead
        ? {
            first_name: lead.first_name || "",
            last_name: lead.last_name || "",
            email: lead.email || "",
            phone: lead.phone || "",
            column_id: resolveColumnId(lead.column_id, columns),
            next_action: lead.next_action || "",
            next_action_at: lead.next_action_at
              ? moment(lead.next_action_at).format("YYYY-MM-DD")
              : "",
          }
        : emptyDetail
    );
  }, [lead?.lead_id]);

  if (!lead) return null;

  const onSubmit = (values: DetailForm) => {
    const patch: UpdateLeadInput = {};
    if (dirtyFields.first_name) patch.first_name = values.first_name;
    if (dirtyFields.last_name) patch.last_name = values.last_name;
    if (dirtyFields.email) patch.email = values.email;
    if (dirtyFields.phone) patch.phone = values.phone;
    if (dirtyFields.column_id) patch.column_id = values.column_id;
    if (dirtyFields.next_action) patch.next_action = values.next_action;
    if (dirtyFields.next_action_at)
      patch.next_action_at = values.next_action_at || null;

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    onSave(patch, onClose);
  };

  const handleDeleteNote = (realIndex: number) => {
    if (isSaving) return;
    onSave({ delete_note: { index: realIndex } }, () => {});
  };

  const errorText = (name: keyof DetailForm) =>
    errors[name]?.message ? (
      <span className="block text-xs text-red-500 mt-1">
        {errors[name]?.message as string}
      </span>
    ) : null;

  const textField = (
    name: keyof DetailForm,
    label: string,
    max: number,
    type = "text",
    showCounter = true
  ) => (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <TextInput
        type={type}
        maxLength={max}
        {...register(name)}
        className="w-full"
      />
      {errorText(name)}
      {showCounter && (
        <CharacterCounter
          currentLength={(watch(name) || "").length}
          maxLength={max}
          className="mt-1"
        />
      )}
    </div>
  );

  return (
    <SideDrawer
      open={!!lead}
      onClose={onClose}
      title={t("leads.detail.title") as string}
      icon={HiOutlineUser}
      footer={
        <div className="sticky bottom-0 left-0 right-0 -mx-4 -mb-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            {canWrite && (
              <CustomButton
                variant="secondary"
                title={t("leads.convert.button") as string}
                onClick={() => onConvert(lead)}
              />
            )}
            <AddToAgendaButton
              getDraft={(author) => leadDraft(getValues(), author, t)}
            />
          </div>
          <div className="flex gap-3">
            <CustomButton
              variant="secondary"
              title={t("common.cancel") as string}
              onClick={onClose}
            />
            {canWrite && (
              <CustomButton
                variant="primary"
                isLoading={isSaving}
                disabled={isSaving}
                title={t("common.saveShort") as string}
                onClick={handleSubmit(onSubmit)}
              />
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {textField(
              "first_name",
              t("leads.addLeadModal.firstName") as string,
              LEAD_FIELD_LIMITS.first_name.max
            )}
            {textField(
              "last_name",
              t("leads.addLeadModal.lastName") as string,
              LEAD_FIELD_LIMITS.last_name.max
            )}
          </div>
          {textField(
            "email",
            t("leads.addLeadModal.email") as string,
            LEAD_FIELD_LIMITS.email.max,
            "email",
            false
          )}

          <div>
            <FieldLabel>{t("leads.addLeadModal.phone")}</FieldLabel>
            <CustomPhoneNumber
              name="phone"
              control={control}
              error={errors.phone}
              helperText={errors.phone?.message as string}
              labelVariant="none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>{t("leads.panel.status")}</FieldLabel>
              <Controller
                name="column_id"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onChange={field.onChange}>
                    {columns.map((c) => (
                      <option key={c.id} value={c.id}>
                        {columnLabel(c, t)}
                      </option>
                    ))}
                  </Select>
                )}
              />
            </div>
            <div>
              <FieldLabel>{t("leads.panel.nextActionAt")}</FieldLabel>
              <TextInput
                type="date"
                {...register("next_action_at")}
                className="w-full"
              />
            </div>
          </div>

          <div>
            <FieldLabel>{t("leads.panel.nextAction")}</FieldLabel>
            <TextInput
              type="text"
              maxLength={MAX_NEXT_ACTION_LENGTH}
              {...register("next_action")}
              placeholder={t("leads.panel.nextActionPlaceholder") as string}
              className="w-full"
            />
            {errorText("next_action")}
            <CharacterCounter
              currentLength={(watch("next_action") || "").length}
              maxLength={MAX_NEXT_ACTION_LENGTH}
              className="mt-1"
            />
          </div>
        </div>

        <div className="flex flex-col">
          <FieldLabel className="mb-2">{t("leads.detail.notes")}</FieldLabel>
          <NotesPanel
            notes={lead.notes || []}
            onAdd={(text, done) => onSave({ add_note: text }, done)}
            onEdit={(index, text, done) =>
              onSave({ edit_note: { index, text } }, done)
            }
            onDelete={(index) => handleDeleteNote(index)}
            maxLength={LEAD_FIELD_LIMITS.note.max}
            isSaving={isSaving}
            disabled={!canWrite}
            canDelete={permissions.can(Cap.LEADS, Level.DELETE)}
            threadHeightClass="h-64"
          />
        </div>
      </div>
    </SideDrawer>
  );
};

export default LeadDetailModal;
